import { addDays, set, startOfWeek } from "date-fns";
import mapValues from "lodash/mapValues";
import { useMemo } from "react";
import useSWR from "swr";

import { getServerUrl } from "config/backend";
import { useChainId } from "lib/chains";
import type { RawIncentivesStats } from "lib/oracleKeeperFetcher";

export default function useIncentiveStats(overrideChainId?: number) {
  const { chainId: defaultChainId } = useChainId();
  const chainId = overrideChainId ?? defaultChainId;

  return (
    useSWR<RawIncentivesStats | null>(["incentiveStats", chainId], async () => {
      const res = await fetch(getServerUrl(chainId, "oracle-keeper/incentives")).then((r) => r.json());
      return res as RawIncentivesStats;
    }).data ?? null
  );
}

export const useLiquidityProvidersIncentives = (chainId: number) => {
  const lpStats = useIncentiveStats(chainId)?.lp;

  return useMemo(() => {
    if (!lpStats || !lpStats.isActive) return null;
    return {
      totalRewards: BigInt(lpStats.totalRewards),
      period: lpStats.period,
      rewardsPerMarket: mapValues(lpStats.rewardsPerMarket, (x) => BigInt(x)),
      token: lpStats.token,
      excludeHolders: lpStats.excludeHolders,
    };
  }, [lpStats]);
};

export type TradingIncentives = ReturnType<typeof useTradingIncentives>;

export const useTradingIncentives = (chainId: number) => {
  const incentiveStats = useIncentiveStats(chainId);

  const startOfPeriod = useMemo(() => {
    const currentDate = new Date();
    const thisWeekWednesday = addDays(startOfWeek(currentDate), 3);
    const wednesday =
      currentDate.valueOf() > thisWeekWednesday.valueOf() ? thisWeekWednesday : addDays(thisWeekWednesday, -7);
    const timezoneOffset = currentDate.getTimezoneOffset() * 60;

    return (
      Math.floor(set(wednesday, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }).valueOf() / 1000) -
      timezoneOffset
    );
  }, []);

  return useMemo(() => {
    const raw = incentiveStats?.trading;
    if (!raw || !raw.isActive) {
      return null;
    }

    // Backend may send rebate percent as:
    // - fraction (0.15 for 15%)
    // - percent (15 for 15%)
    // - basis points (1500 for 15%)
    // Normalize to basis points (integer) as BigInt for UI math.
    const toBpsBigInt = (val: number) => {
      if (Number.isNaN(val)) return 0n;
      let bps: number;
      if (val <= 1 && val >= 0) {
        bps = Math.round(val * 10000);
      } else if (val <= 100 && val >= -100) {
        bps = Math.round(val * 100);
      } else {
        bps = Math.round(val);
      }
      return BigInt(bps);
    };

    const maxRebatePercent = toBpsBigInt(raw.maxRebatePercent);
    if (maxRebatePercent === 0n) return null;

    const estimatedRebatePercent = toBpsBigInt(raw.estimatedRebatePercent);

    const allocation = BigInt(raw.allocation);
    const nextPeriodStart = addDays(new Date(startOfPeriod * 1000), 7);

    return {
      allocation,
      period: raw.period,
      nextPeriodStart,
      maxRebatePercent,
      estimatedRebatePercent,
      token: raw.token,
    };
  }, [incentiveStats, startOfPeriod]);
};
